// Electron Database Manager for ACCTOO
// Combines SQLite and Firebase 2-way sync for Electron environment

import ElectronSQLiteManager from './ElectronSQLiteManager.js';
import ElectronSyncManager from './ElectronSyncManager.js';

class ElectronDatabaseManager {
  constructor() {
    this.sqliteManager = new ElectronSQLiteManager();
    this.syncManager = new ElectronSyncManager(this.sqliteManager);
    this.isInitialized = false;
    this.userId = null;
    this.companyId = null;
    this.deviceId = null;
  }

  // =============================================
  // INITIALIZATION
  // =============================================

  // Initialize database system
  async init(userId, companyId, deviceId) {
    try {
      console.log('🔄 Initializing Electron Database System...', { userId, companyId, deviceId });

      this.userId = userId;
      this.companyId = companyId;
      this.deviceId = deviceId;

      // Initialize SQLite
      await this.sqliteManager.init(userId, companyId, deviceId);

      // Initialize sync manager
      this.syncManager.init(companyId, deviceId);

      this.isInitialized = true;
      console.log('✅ Electron Database System initialized successfully');

      // Perform initial sync if online
      if (navigator.onLine) {
        setTimeout(() => this.syncManager.performSync(), 2000); // Delay to allow UI to load
      }

      return { success: true };
    } catch (error) {
      console.error('❌ Electron Database initialization failed:', error);
      throw error;
    }
  }

  // =============================================
  // BUSINESS DATA OPERATIONS
  // =============================================

  // Generic data operations
  async insert(tableName, data) {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    try {
      // Insert into SQLite first
      const result = await this.sqliteManager.insert(tableName, data);

      // Add to sync queue for Firebase upload
      await this.sqliteManager.insert('sync_queue', {
        company_id: this.companyId,
        device_id: this.deviceId,
        table_name: tableName,
        record_id: data.id,
        operation: 'create',
        data: JSON.stringify(data),
        status: 'pending'
      });

      // Trigger sync if online
      if (navigator.onLine) {
        setTimeout(() => this.syncManager.performSync(), 1000);
      }

      return result;
    } catch (error) {
      console.error(`❌ Insert failed for ${tableName}:`, error);
      throw error;
    }
  }

  async update(tableName, id, data) {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    try {
      // Update in SQLite first
      const result = await this.sqliteManager.update(tableName, id, data);

      // Add to sync queue for Firebase upload
      await this.sqliteManager.insert('sync_queue', {
        company_id: this.companyId,
        device_id: this.deviceId,
        table_name: tableName,
        record_id: id,
        operation: 'update',
        data: JSON.stringify(data),
        status: 'pending'
      });

      // Trigger sync if online
      if (navigator.onLine) {
        setTimeout(() => this.syncManager.performSync(), 1000);
      }

      return result;
    } catch (error) {
      console.error(`❌ Update failed for ${tableName}:`, error);
      throw error;
    }
  }

  async select(tableName, filters = {}) {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    try {
      return await this.sqliteManager.select(tableName, {
        company_id: this.companyId,
        ...filters
      });
    } catch (error) {
      console.error(`❌ Select failed for ${tableName}:`, error);
      throw error;
    }
  }

  async delete(tableName, id) {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    try {
      // Delete from SQLite first
      const result = await this.sqliteManager.delete(tableName, id);

      // Add to sync queue for Firebase deletion
      await this.sqliteManager.insert('sync_queue', {
        company_id: this.companyId,
        device_id: this.deviceId,
        table_name: tableName,
        record_id: id,
        operation: 'delete',
        data: JSON.stringify({ id }),
        status: 'pending'
      });

      // Trigger sync if online
      if (navigator.onLine) {
        setTimeout(() => this.syncManager.performSync(), 1000);
      }

      return result;
    } catch (error) {
      console.error(`❌ Delete failed for ${tableName}:`, error);
      throw error;
    }
  }

  // =============================================
  // BILL NUMBER GENERATION
  // =============================================

  // Generate bill number
  async generateBillNumber(billType) {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    try {
      return await this.sqliteManager.generateBillNumber(billType);
    } catch (error) {
      console.error('❌ Generate bill number failed:', error);
      throw error;
    }
  }

  // =============================================
  // SYNC OPERATIONS
  // =============================================

  // Manual sync trigger
  async sync() {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    try {
      return await this.syncManager.performSync();
    } catch (error) {
      console.error('❌ Sync failed:', error);
      throw error;
    }
  }

  // Get sync status
  async getSyncStatus() {
    if (!this.isInitialized) {
      return { isOnline: false, syncInProgress: false };
    }

    try {
      return await this.syncManager.getSyncStatus();
    } catch (error) {
      console.error('❌ Get sync status failed:', error);
      throw error;
    }
  }

  // Get sync history
  async getSyncHistory(limit = 50) {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    try {
      return await this.syncManager.getSyncHistory(limit);
    } catch (error) {
      console.error('❌ Get sync history failed:', error);
      throw error;
    }
  }

  // =============================================
  // DEVICE MANAGEMENT
  // =============================================

  // Register device
  async registerDevice(deviceType, deviceName, counterId = '') {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    try {
      let deviceId;

      if (deviceType === 'online') {
        deviceId = `${this.companyId}_Online`;
      } else {
        // Generate unique device ID
        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substring(2, 8);
        deviceId = `${deviceType.toUpperCase()}_${timestamp}_${randomId}`;
      }

      const deviceData = {
        id: deviceId,
        device_id: deviceId,
        company_id: this.companyId,
        device_type: deviceType,
        device_name: deviceName,
        bill_prefix: deviceType === 'master' ? 'MA' : deviceType === 'online' ? 'ON' : counterId || 'CO',
        bill_counter: deviceType === 'online' ? 'ONLINE' : counterId || deviceId,
        status: 'active'
      };

      // Save to SQLite
      await this.sqliteManager.insert('devices', deviceData);

      // Setup bill sequences
      await this.sqliteManager.setupBillSequences(this.companyId, deviceId, deviceData.bill_counter);

      console.log(`✅ Device registered: ${deviceId} (${deviceType})`);
      return { success: true, deviceId, deviceData };
    } catch (error) {
      console.error('❌ Device registration failed:', error);
      throw error;
    }
  }

  // Get company devices
  async getCompanyDevices() {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    try {
      return await this.sqliteManager.select('devices', {
        company_id: this.companyId
      });
    } catch (error) {
      console.error('❌ Get company devices failed:', error);
      throw error;
    }
  }

  // =============================================
  // UTILITY METHODS
  // =============================================

  // Check if initialized
  isReady() {
    return this.isInitialized;
  }

  // Get current context
  getContext() {
    return {
      userId: this.userId,
      companyId: this.companyId,
      deviceId: this.deviceId,
      environment: 'electron',
      isOnline: navigator.onLine
    };
  }

  // Close database
  async close() {
    if (!this.isInitialized) {
      return;
    }

    try {
      await this.sqliteManager.close();
      this.isInitialized = false;
      console.log('✅ Electron Database closed');
    } catch (error) {
      console.error('❌ Close database failed:', error);
      throw error;
    }
  }

  // Get database statistics
  async getDatabaseStats() {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    try {
      return await this.sqliteManager.getDatabaseStats();
    } catch (error) {
      console.error('❌ Get database stats failed:', error);
      throw error;
    }
  }
}

// Create singleton instance
const electronDatabaseManager = new ElectronDatabaseManager();

export default electronDatabaseManager;
